import { Info } from 'lucide-react';
import { Button, Card, Description, Popover } from '@heroui/react';
import type { LeaderboardCategory, LeaderboardCategoryKey } from '../../../api/dashboard';
import { UserAvatar } from '../../../components/ui/user-avatar';

interface LeaderboardCategoryCardProps {
  category: LeaderboardCategory;
}

const CATEGORY_HELP: Record<LeaderboardCategoryKey, string> = {
  available:
    'Janela disponibilizada e usada vale 10 pontos. Se ninguém usar, vale 3 pontos. Os pontos vão para quem abriu a disponibilidade.',
  borrowed:
    'Cada vez que a pessoa pega emprestado a vaga de outro condômino (empréstimo aprovado ou finalizado) vale 5 pontos.',
};

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <span
        aria-label="1º lugar"
        className="flex size-7 shrink-0 items-center justify-center rounded-full bg-warning/15 text-sm"
      >
        🏆
      </span>
    );
  }

  const tone =
    rank === 2
      ? 'bg-default text-muted'
      : rank === 3
        ? 'bg-accent/10 text-accent'
        : 'bg-surface-secondary text-muted';

  return (
    <span
      className={`flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${tone}`}
    >
      {rank}
    </span>
  );
}

function EntryRow({
  entry,
  unit,
}: {
  entry: LeaderboardCategory['entries'][number];
  unit: string;
}) {
  return (
    <div
      className={`flex items-start gap-3 rounded-lg px-2 py-2 ${
        entry.isCurrentUser ? 'bg-accent/10' : ''
      }`}
    >
      <RankBadge rank={entry.rank} />
      <UserAvatar
        name={entry.displayName}
        photoUrl={entry.photoUrl}
        size="sm"
        className="shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground">
          {entry.displayName}
        </p>
        {entry.isCurrentUser ? (
          <p className="text-xs font-normal text-accent">você</p>
        ) : null}
      </div>
      <p className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
        {entry.score}
        <span className="ml-1 text-xs font-normal text-muted">{unit}</span>
      </p>
    </div>
  );
}

function CategoryInfoButton({ category }: { category: LeaderboardCategory }) {
  return (
    <Popover>
      <Popover.Trigger>
        <Button
          isIconOnly
          size="sm"
          variant="secondary"
          aria-label={`Como funciona: ${category.title}`}
          className="size-7 min-w-7 rounded-full"
        >
          <Info size={14} />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        placement="bottom"
        offset={8}
        className="z-50 w-[min(18rem,calc(100vw-2rem))] border border-separator bg-overlay shadow-overlay"
      >
        <Popover.Dialog className="p-3">
          <Popover.Heading className="mb-1.5 text-sm font-semibold leading-snug">
            {category.title}
          </Popover.Heading>
          <p className="text-sm leading-relaxed text-muted">{CATEGORY_HELP[category.key]}</p>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

export function LeaderboardCategoryCard({ category }: LeaderboardCategoryCardProps) {
  const showCurrentOutsideTop =
    category.currentUser != null &&
    category.currentUser.score > 0 &&
    category.currentUser.rank > category.entries.length;

  return (
    <Card>
      <Card.Header className="border-b border-separator pb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Card.Title className="text-base font-semibold leading-snug">
              {category.title}
            </Card.Title>
            <Card.Description className="mt-0.5">Top 5 do período</Card.Description>
          </div>
          <CategoryInfoButton category={category} />
        </div>
      </Card.Header>
      <Card.Content className="flex flex-col gap-1 pt-3">
        {category.entries.length === 0 ? (
          <Description className="py-6 text-center text-sm">
            Ainda não há dados neste ranking.
          </Description>
        ) : (
          category.entries.map((entry) => (
            <EntryRow key={entry.userId} entry={entry} unit={category.unit} />
          ))
        )}

        {showCurrentOutsideTop && category.currentUser ? (
          <>
            <div className="my-1 border-t border-dashed border-separator" />
            <EntryRow entry={category.currentUser} unit={category.unit} />
          </>
        ) : null}
      </Card.Content>
    </Card>
  );
}
